'use client';

import classNames from 'classnames';
import Image from 'next/image';

import type {CourseImages} from '@udemy-v2/browse-course';
import {Button} from '@udemy-v2/react-core-components';
import {PlayOverlay} from '@udemy-v2/react-structure-components';
import React, {useState, useMemo, useCallback} from 'react';
import {useI18n} from '@udemy-v2/i18n';
import {ColorScheme} from '@udemy-v2/styles';
import styles from './course-preview-entry-point.module.scss';
import {useClpCourse} from '../../context/clp-course-provider.react-component';
import {CoursePreviewModal} from '../course-preview/course-preview-modal.react-component';
import {usePositionMarker} from '@/components/position-marker';
import {useAssetImage} from '@/features/course-landing-page/context/asset-image-provider.react-component';

// Base 64 encoded 500x500 transparent placeholder image used for LCP optimization
const placeholderBase64500x500 =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAfQAAAH0CAIAAABEtEjdAAABgmlDQ1BzUkdCIElFQzYxOTY2LTIuMQAAKJF1kc8rRFEUxz8zgxGjERaUxaRhhcYosVFGQk2axii/NjPP/FDzxuu9mSRbZTtFiY1fC/4CtspaKSIlO2VNbNBz3oyaSebczj2f+733nO49F+yRtKIaVT5QM1k9PB7wzM7Ne5zP1NBGE63YooqhjYRCQSraxx02K970WLUqn/vX6pfihgK2WuFhRdOzwhPCwdWsZvG2cIuSii4Jnwp363JB4VtLjxX5xeJkkb8s1iPhUbA3CnuSZRwrYyWlq8LycrxqOqf83sd6iSuemZmW2CHejkGYcQJ4mGSMUQboY0jmAXrw0ysrKuT7CvlTrEiuIrPGGjrLJEmRpVvUnFSPS0yIHpeRZs3q/9++Gol+f7G6KwDVT6b51gnOLfjOm+bnoWl+H4HjES4ypfyVAxh8Fz1f0rz74N6As8uSFtuB801ofdCierQgOcTtiQS8nkDDHDRfQ91CsWe/+xzfQ2RdvuoKdvegS867F38AGNNnwvJxPYUAAAAJcEhZcwAACxMAAAsTAQCanBgAAALtSURBVHic7cEBAQAAAIIg/69uSEABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/Bh0SQABe+rNwwAAAABJRU5ErkJggg==';

interface AssetImageProps {
    images: CourseImages;
    sizes?: string;
}

export const AssetImage = ({
    images,
    sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
}: AssetImageProps) => {
    // Use the highest quality image available as the primary source
    const primarySrc = images.px480x270 || images.px304x171 || images.px240x135;

    if (!primarySrc) {
        return null;
    }

    return (
        <Image
            src={primarySrc}
            alt=""
            fill
            style={{
                objectFit: 'cover',
            }}
            sizes={sizes}
            priority={true}
            placeholder="blur"
            blurDataURL={placeholderBase64500x500}
        />
    );
};

interface CoursePreviewEntryPointProps {
    // TODO: Determine what image sizes to use for the image
    imageSizes?: string;
    lectureId?: string;
    // UI region for event tracking - will be passed to event tracking in CoursePreviewModal
    uiRegion: string;
    // Whether to hide/fade this component when CourseLede goes out of view
    hideWhenLedeOutOfView?: boolean;
}

/**
 * A wrapper component that handles responsive rendering of CoursePreviewEntryPoint
 * to prevent layout shift and unwanted flashing on desktop.
 *
 * This component always renders but uses CSS media queries to control visibility,
 * ensuring consistent layout behavior across device types.
 */
export const CoursePreviewEntryPointWrapper = ({
    uiRegion,
    hideWhenLedeOutOfView = false,
    lectureId,
}: CoursePreviewEntryPointProps) => {
    return (
        <div className={styles.placeholder}>
            {/* Mobile/Tablet: Always visible */}
            <div className={styles['mobile-preview']}>
                <CoursePreviewEntryPoint
                    uiRegion={uiRegion}
                    hideWhenLedeOutOfView={hideWhenLedeOutOfView}
                    lectureId={lectureId}
                />
            </div>

            {/* Desktop: Hidden placeholder to prevent layout shift */}
            <div className={styles['desktop-placeholder']} aria-hidden="true" />
        </div>
    );
};

const CoursePreviewEntryPointInternal = ({
    lectureId,
    uiRegion,
    hideWhenLedeOutOfView = true,
}: CoursePreviewEntryPointProps) => {
    const course = useClpCourse();
    const {promoVideoAsset} = course;
    const {gettext} = useI18n();
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const {isComponentInView} = usePositionMarker();
    const {preloadedAssetImage} = useAssetImage();

    // Determine if component should be visible based on configuration
    // Derive visibility directly instead of syncing via effect
    const isVisible = hideWhenLedeOutOfView ? isComponentInView : true;

    const showPreviewModal = useCallback(() => {
        setIsPreviewModalOpen(true);
    }, [setIsPreviewModalOpen]);

    const hidePreviewModal = useCallback(() => {
        setIsPreviewModalOpen(false);
    }, [setIsPreviewModalOpen]);

    // Memoize the preview entry point content to prevent re-renders of static elements
    // like PlayOverlay, which can cause visual flashing
    const previewEntryPoint = useMemo(
        () => (
            <>
                <div className={styles.asset}>
                    {promoVideoAsset && (
                        <Button
                            className={classNames(
                                'ud-custom-focus-visible',
                                'ud-link-knockout',
                                styles['placeholder-btn'],
                            )}
                            onClick={showPreviewModal}
                            udStyle="ghost"
                            aria-label={gettext('Play course preview')}
                        >
                            <span className={styles['img-aspect']}>{preloadedAssetImage}</span>
                            <span className={classNames(styles.overlay, styles.gradient)} />
                            <PlayOverlay size="xxlarge" />
                            <span
                                className={classNames('ud-heading-md', styles.overlay, styles.text)}
                            >
                                {gettext('Preview this course')}
                            </span>
                        </Button>
                    )}
                    {!promoVideoAsset && (
                        <div className={styles['placeholder-img']}>
                            <div className={styles['img-aspect']}>{preloadedAssetImage}</div>
                        </div>
                    )}
                </div>
                {/** Modal is placed within ColorScheme to inherit dark color scheme */}
                <CoursePreviewModal
                    onClose={hidePreviewModal}
                    isOpen={isPreviewModalOpen}
                    uiRegion={uiRegion}
                    selectedLectureId={lectureId}
                />
            </>
        ),
        [
            promoVideoAsset,
            showPreviewModal,
            gettext,
            preloadedAssetImage,
            hidePreviewModal,
            isPreviewModalOpen,
            uiRegion,
            lectureId,
        ],
    );

    // If hideWhenLedeOutOfView is disabled, render without transition container
    if (!hideWhenLedeOutOfView) {
        return (
            <ColorScheme colorScheme="dark" className={styles.wrapper}>
                {previewEntryPoint}
            </ColorScheme>
        );
    }

    return (
        <div
            className={classNames(styles['transition-container'], {
                [styles['collapsed']]: !isVisible,
            })}
        >
            <ColorScheme
                colorScheme="dark"
                className={classNames(styles.wrapper, {
                    [styles['fade-out']]: !isVisible,
                })}
            >
                {previewEntryPoint}
            </ColorScheme>
        </div>
    );
};

// Memoize the component to prevent re-renders when props don't change
// This is critical for LCP performance when the component is used in the sidebar
export const CoursePreviewEntryPoint = React.memo(CoursePreviewEntryPointInternal);
